/**
 * Plugins: what is installed, what each may do and adds, on/off, edit, export,
 * log and remove; new ones with AI, by hand or from a file (ADR 0025).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileCode2, FileUp, Plus, ScrollText, Sparkles, Trash2 } from 'lucide-react';
import {
  exportPlugin,
  parsePluginFile,
  savePlugin,
  setPluginEnabled,
  type InstalledPlugin,
} from '@nexus/plugins';
import { useStore } from '../db/context';
import { ConfirmDialog, Dialog } from '../components/Dialog';
import { saveFile } from '../lib/export';
import { checkPlugin, usePluginEditor } from './editorState';
import { useBuilder } from './builderState';
import { unloadPlugin, usePluginLog, usePlugins } from './runtime';
import { ModuleIcon } from '../components/ModuleIcon';

/** Stable empty list: a new [] per render would make the store selector loop. */
const NO_LINES: string[] = [];

const iconButton =
  'grid size-7 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg';

export function PluginsView() {
  const { t } = useTranslation();
  const store = useStore();
  const plugins = usePlugins();
  const [removing, setRemoving] = useState<InstalledPlugin | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const importFile = async (f: File) => {
    setImportError(null);
    const parsed = parsePluginFile(await f.text());
    if ('error' in parsed) return setImportError(parsed.error);
    const problem = await checkPlugin(parsed.manifest, parsed.code);
    if (problem) return setImportError(problem);
    await savePlugin(store, { ...parsed, source: 'import', enabled: false });
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10" data-testid="plugins">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
            <ModuleIcon kind="plugins" size="lg" />
            {t('plugins.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('plugins.intro')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => useBuilder.getState().open(null)}
            className="flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg"
          >
            <Sparkles className="size-4" />
            {t('plugins.newWithAi')}
          </button>
          <button
            type="button"
            onClick={() => usePluginEditor.getState().set('new')}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-bg-hover"
          >
            <Plus className="size-4" />
            {t('plugins.newByHand')}
          </button>
          <button
            type="button"
            onClick={() => file.current?.click()}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-bg-hover"
          >
            <FileUp className="size-4" />
            {t('plugins.import')}
          </button>
          <input
            ref={file}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void importFile(f);
            }}
          />
        </div>
      </header>
      {importError && (
        <p role="alert" className="mb-4 text-sm text-danger">
          {importError}
        </p>
      )}
      {plugins?.length === 0 && <p className="text-sm text-fg-muted">{t('plugins.none')}</p>}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {plugins?.map((p) => (
          <Card key={p.pageId} plugin={p} onRemove={() => setRemoving(p)} />
        ))}
      </div>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={t('plugins.removeTitle', { name: removing?.manifest.name ?? '' })}
        description={t('plugins.removeText')}
        confirmLabel={t('plugins.remove')}
        cancelLabel={t('plugins.cancel')}
        onConfirm={() => {
          if (removing) {
            unloadPlugin(removing.pageId);
            void store.trashPage(removing.pageId);
          }
          setRemoving(null);
        }}
      />
    </div>
  );
}

function Card({ plugin, onRemove }: { plugin: InstalledPlugin; onRemove: () => void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [showLog, setShowLog] = useState(false);
  // Flips at once; the stored value follows.
  const [on, setOn] = useState<boolean | null>(null);
  const enabled = on ?? plugin.enabled;
  const log = usePluginLog((s) => s.lines[plugin.pageId]) ?? NO_LINES;
  const m = plugin.manifest;
  const adds = [
    ...m.blocks.map((x) => t('plugins.adds.block', { title: x.title })),
    ...m.commands.map((x) => t('plugins.adds.command', { title: x.title })),
    ...m.views.map((x) => t('plugins.adds.view', { title: x.title })),
    ...m.automations.map((x) => t('plugins.adds.automation', { title: x.title })),
  ];

  return (
    <article
      aria-label={m.name}
      data-testid="plugin-card"
      className="flex flex-col gap-2 rounded-xl border border-border p-3"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-medium">
            {m.name} <span className="text-xs font-normal text-fg-muted">v{m.version}</span>
          </h2>
          {m.description && <p className="text-sm text-fg-muted">{m.description}</p>}
        </div>
        <label className="flex shrink-0 items-center gap-1 text-xs">
          <input
            type="checkbox"
            role="switch"
            checked={enabled}
            onChange={(e) => {
              const next = e.target.checked;
              setOn(next);
              unloadPlugin(plugin.pageId);
              void setPluginEnabled(store, plugin.pageId, next).finally(() => setOn(null));
            }}
          />
          {t('plugins.enabled')}
        </label>
      </div>
      <p className="flex flex-wrap gap-1 text-xs">
        <span className="rounded bg-bg-subtle px-1.5 py-0.5">
          {t(`plugins.source.${plugin.source}`)}
        </span>
        {m.permissions.read && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">{t('plugins.perm.read')}</span>
        )}
        {m.permissions.write && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">{t('plugins.perm.write')}</span>
        )}
        {m.permissions.network.map((d) => (
          <span key={d} className="rounded bg-amber-500/15 px-1.5 py-0.5">
            🌐 {d}
          </span>
        ))}
      </p>
      <ul className="list-inside list-disc text-xs text-fg-muted">
        {adds.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>
      <div className="mt-auto flex items-center gap-1">
        {plugin.source === 'ai' && (
          <button
            type="button"
            onClick={() => useBuilder.getState().open(plugin)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-bg-hover"
          >
            <Sparkles className="size-3.5" />
            {t('plugins.refine')}
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => usePluginEditor.getState().set(plugin)}
          aria-label={t('plugins.edit', { name: m.name })}
          title={t('plugins.edit', { name: m.name })}
          className={iconButton}
        >
          <FileCode2 className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setShowLog(true)}
          aria-label={t('plugins.log', { name: m.name })}
          title={t('plugins.log', { name: m.name })}
          className={iconButton}
        >
          <ScrollText className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() =>
            void saveFile(
              `${m.id}.nexus-plugin.json`,
              new TextEncoder().encode(exportPlugin(plugin)),
              'application/json',
              { name: 'Nexus-plugin', extensions: ['json'] },
            )
          }
          aria-label={t('plugins.export', { name: m.name })}
          title={t('plugins.export', { name: m.name })}
          className={iconButton}
        >
          <Download className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('plugins.removeOf', { name: m.name })}
          title={t('plugins.removeOf', { name: m.name })}
          className={`${iconButton} hover:text-danger`}
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      <Dialog open={showLog} onOpenChange={setShowLog} title={t('plugins.log', { name: m.name })}>
        <pre className="max-h-96 overflow-auto rounded-md bg-bg-subtle p-2 text-xs whitespace-pre-wrap">
          {log.length ? log.join('\n') : t('plugins.logEmpty')}
        </pre>
      </Dialog>
    </article>
  );
}
